import { useState, useEffect } from 'react';
import { Building2, Check, X, Loader, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';

interface Module {
  id: string;
  name: string;
  slug: string;
  description: string;
  features: string[];
}

interface ModuleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onModuleSelected: () => void;
  required?: boolean;
}

export function ModuleSelectionModal({ isOpen, onClose, onModuleSelected, required = false }: ModuleSelectionModalProps) {
  const { selectModule: selectModuleFromHook } = usePermissions();
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModuleSlug, setSelectedModuleSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchModules();
    }
  }, [isOpen]);

  async function fetchModules() {
    try {
      const { data, error } = await supabase
        .from('modules_metiers')
        .select('*')
        .order('name');

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!selectedModuleSlug) return;

    setSaving(true);
    try {
      await selectModuleFromHook(selectedModuleSlug);
      onModuleSelected();
      onClose();
    } catch (error) {
      console.error('Error activating module:', error);
      alert('Erreur lors de l\'activation du module');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !required) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-sky-50">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 size={28} className="text-blue-600" />
            Choisissez votre module métier {required && <span className="text-red-600">*</span>}
          </h2>
          <p className="text-gray-600 mt-2">
            {required
              ? "Votre abonnement inclut l'accès à un module métier spécialisé. Vous devez sélectionner votre module pour continuer."
              : "Votre abonnement inclut l'accès à un module métier spécialisé. Sélectionnez celui qui correspond le mieux à votre activité."
            }
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {modules.map((module) => (
                <button
                  key={module.id}
                  onClick={() => setSelectedModuleSlug(module.slug)}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    selectedModuleSlug === module.slug
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-lg text-gray-900">{module.name}</h3>
                    {selectedModuleSlug === module.slug && (
                      <div className="bg-blue-600 text-white rounded-full p-1">
                        <Check size={16} />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{module.description}</p>
                  <div className="space-y-2">
                    {(module.features as unknown as string[]).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              <strong>Remarque :</strong> Vous pouvez modifier votre module métier à tout moment depuis vos paramètres.
              Un seul module peut être actif simultanément.
            </p>
          </div>

          {required && !selectedModuleSlug && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-900 font-medium">
                Veuillez sélectionner un module pour continuer à utiliser l'application.
              </p>
            </div>
          )}

          <div className={`flex items-center pt-4 border-t border-gray-200 ${required ? 'justify-end' : 'justify-between'}`}>
            {!required && (
              <button
                onClick={onClose}
                disabled={saving}
                className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <span className="flex items-center gap-2">
                  <X size={20} />
                  Plus tard
                </span>
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={!selectedModuleSlug || saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold transition-colors"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <Loader className="animate-spin" size={20} />
                  Activation...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Activer le module
                  <ArrowRight size={20} />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
